
import { mapState, mapMutations } from 'vuex';
import { hydrateWhenVisible } from 'nuxt-lazy-hydration';
import { calculateStockAmount } from '@/utils/stock';

export default {
  name: 'Product',

  components: {
    ProductTags: hydrateWhenVisible(() =>
      import('@/components/shop/productTags.vue')
    ),
    WpImage: hydrateWhenVisible(() => import('@/components/ui/img.vue')),
  },

  filters: {
    currency(value, currency) {
      return value ? `${value} ${currency}` : null;
    },
  },

  props: {
    product: {
      type: Object,
      required: true,
    },
    loading: {
      type: String,
      default: 'lazy',
    },
  },

  data() {
    return {
      isFavorite: false,
      isLoading: false,
      stockAmount: -1,
    };
  },

  computed: {
    ...mapState([
      'currency',
      'patientId',
      'user',
      'myFavorites',
      'loginOpened',
    ]),

    rating() {
      return Math.round(parseFloat(this.product.average_rating));
    },

    quantity: {
      get() {
        return this.product
          ? this.$store.getters.getQuantity(this.product.sku)
          : 0;
      },
      set(value) {
        const quantity = parseInt(value);
        if (value > 0 && this.quantity > 0) {
          this.editCartItem({
            quantity,
            sku: this.product.sku,
          });
        } else if (value > 0) {
          this.insertToCart(quantity);
        } else {
          this.removeCartItem(this.product);
        }
      },
    },

    image() {
      return this.product && this.product.images.length
        ? this.product.images[0]
        : null;
    },

    price() {
      return this.$store.getters.getActualPrice(this.product.sku);
    },

    originalPrice() {
      return this.$store.getters.getPrice(this.product.sku);
    },

    onSale() {
      return (
        this.product &&
        this.originalPrice &&
        this.price &&
        this.originalPrice > this.price
      );
    },
  },

  mounted() {
    if (this.product) {
      this.getStockAmount(this.product.sku);
    }
    if (this.user && this.myFavorites && this.myFavorites.length) {
      const myFavoritesIds = this.myFavorites.map((f) => f.sage_id);
      this.isFavorite = myFavoritesIds.includes(this.product.sku);
    }
  },

  methods: {
    ...mapMutations([
      'addToCart',
      'editCartItem',
      'removeCartItem',
      'openLoginModal',
      'updateFavorites',
      'openCart',
    ]),

    async getStockAmount(sku) {
      try {
        const { data } = await this.$bolev.getProductStock({
          sku,
          patientId: this.patientId,
        });
        this.stockAmount = calculateStockAmount(data);
      } catch (_) {
        this.errorMessage = 'Something went wrong';
      }
    },

    getPrice() {
      this.store.getters.getActualPrice(this.product.sku);
    },

    async submit() {
      this.isLoading = true;
      if (!this.isFavorite) {
        try {
          await this.$bolev.addToFavorites({
            sku: this.product.sku,
            patient_id: this.patientId,
          });
          this.isFavorite = true;
          this.isLoading = false;
          this.updateFavorites(this.product.sku);
        } catch (_) {
          this.isLoading = false;
          this.errorMessage = 'Something went wrong';
        }
      } else {
        try {
          await this.$bolev.removeFromFavorites({
            sku: this.product.sku,
            patient_id: this.patientId,
          });
          this.isFavorite = false;
          this.isLoading = false;
          this.updateFavorites(this.product.sku);
        } catch (_) {
          this.isLoading = false;
          this.errorMessage = 'Something went wrong';
        }
      }
    },

    isNumber(evt) {
      evt = evt || window.event;
      const charCode = evt.which || evt.keyCode;
      if (
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.preventDefault();
      } else {
        return true;
      }
    },

    addOne() {
      if (this.stockAmount > this.quantity) {
        this.openCart();
        if (this.quantity === 0) {
          this.insertToCart(1);
        } else {
          this.editCartItem({
            quantity: this.quantity + 1,
            sku: this.product.sku,
          });
        }
      }
    },

    removeOne() {
      if (this.quantity < 1) return;
      if (this.quantity === 1) {
        this.removeCartItem(this.product);
      } else {
        this.editCartItem({
          quantity: this.quantity - 1,
          sku: this.product.sku,
        });
      }
    },

    insertToCart(q) {
      if (this.quantity === 0) {
        this.addToCart({
          quantity: q,
          sku: this.product.sku,
          regularPrice: this.$store.getters.getPrice(this.product.sku),
          price: this.$store.getters.getActualPrice(this.product.sku),
          onSale: this.product.on_sale,
          salePrice: this.product.sale_price,
          categories: this.product.categories.map((c) => c.id),
        });
        this.$bolev.metaEvent({
          patientId: this.patientId,
          type: 'addToCart',
          quantity: q,
          sku: this.product.sku,
          price: this.$store.getters.getActualPrice(this.product.sku),
        });
      }
    },
  },
};
